import { useEffect, useRef } from 'react';
import { makePointCloud } from './MonadSigil';

// Four small looping animations, one per "What MONAD does" beat. Each is ONE
// cohesive, connected, looping piece (no scenes / chrome):
//  - objective: an ask-composer (type a goal, attach data, run)
//  - domain:    a single console that boots (GPU/CPU/storage), mounts the 82
//               databases, then runs analysis in its terminal, on a loop
//  - orchestration / resolution: radial dispatch + report (placeholder-grade)
// All respect prefers-reduced-motion.

const CX = 120;
const CY = 90;

// Orchestration: a branching tree that grows out from the MONAD core. The layout
// is generative with a deterministic hash (no Math.random), so it is asymmetric
// and organic, not a perfectly even fan: jittered angles/distances and a varying
// number of children per node. Node order is breadth-first by generation, so the
// tree can be revealed one generation at a time.
const orchHash = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
function buildOrchTree() {
  let seed = 1;
  const rnd = () => {
    const v = orchHash(seed);
    seed += 1;
    return v;
  };
  const nodes = [{ x: 0, y: 0, gen: 0, parent: -1, ang: 0, r1: -1 }];
  const add = (parent, ang, dist, gen) => {
    const p = nodes[parent];
    nodes.push({
      x: +(p.x + Math.cos(ang) * dist).toFixed(1),
      y: +(p.y + Math.sin(ang) * dist).toFixed(1),
      gen,
      parent,
      ang,
      r1: gen === 1 ? nodes.length : p.r1, // index of the gen-1 ancestor (subtree id)
    });
    return nodes.length - 1;
  };
  let prev = [];
  for (let i = 0; i < 6; i += 1) {
    const a = -Math.PI / 2 + (i / 6) * Math.PI * 2 + (rnd() - 0.5) * 0.5;
    prev.push(add(0, a, 36 + rnd() * 9, 1));
  }
  const SPEC = [
    { dist: 31, spread: 1.2, min: 2, max: 3 }, // gen 2
    { dist: 25, spread: 1.0, min: 1, max: 3 }, // gen 3
    { dist: 20, spread: 0.85, min: 0, max: 2 }, // gen 4
  ];
  SPEC.forEach((s, gi) => {
    const gen = gi + 2;
    const next = [];
    prev.forEach((pi) => {
      const pa = nodes[pi].ang;
      const count = s.min + Math.floor(rnd() * (s.max - s.min + 1));
      for (let j = 0; j < count; j += 1) {
        const frac = count === 1 ? 0.5 : j / (count - 1);
        const a = pa + (-s.spread / 2 + s.spread * frac) + (rnd() - 0.5) * 0.42;
        next.push(add(pi, a, s.dist + rnd() * 8, gen));
      }
    });
    prev = next;
  });
  return nodes;
}
const ORCH = buildOrchTree();
const ORCH_RADIUS = {};
[1, 2, 3, 4].forEach((g) => {
  const rs = ORCH.filter((n) => n.gen === g).map((n) => Math.hypot(n.x, n.y));
  ORCH_RADIUS[g] = rs.length ? Math.max(...rs) : 0;
});

// A few cross-branch "merge" edges: close node pairs from different gen-1
// subtrees, so separate branches visibly combine as the tree fills in.
const ORCH_LINKS = [];
for (let i = 1; i < ORCH.length && ORCH_LINKS.length < 7; i += 1) {
  if (ORCH[i].gen >= 2) {
    for (let j = i + 1; j < ORCH.length; j += 1) {
      if (ORCH[j].gen >= 2 && ORCH[i].r1 !== ORCH[j].r1) {
        const d = Math.hypot(ORCH[i].x - ORCH[j].x, ORCH[i].y - ORCH[j].y);
        if (d > 6 && d < 19) {
          ORCH_LINKS.push({ a: i, b: j, len: +d.toFixed(1) });
          break;
        }
      }
    }
  }
}

// Tiny abstract "terminals" beside a scattering of mid-tree nodes, at most one
// per branch so they spread out rather than clustering on sibling nodes.
const ORCH_TERMS = [];
const ORCH_TERM_PARENTS = new Set();
ORCH.forEach((n, i) => {
  if (
    (n.gen === 2 || n.gen === 3) &&
    orchHash(i * 2.7 + 5) > 0.58 &&
    !ORCH_TERM_PARENTS.has(n.parent) &&
    ORCH_TERMS.length < 6
  ) {
    ORCH_TERM_PARENTS.add(n.parent);
    ORCH_TERMS.push(i);
  }
});

function FileIcon() {
  return (
    <svg className="WSMONAD-oa-fi" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 1.6h4.8L12 4.8V14a.4.4 0 0 1-.4.4H4.4A.4.4 0 0 1 4 14z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M8.6 1.6v3.2H12" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  );
}
const FILE_SVG =
  '<svg class="WSMONAD-oa-fi" viewBox="0 0 16 16" aria-hidden="true">' +
  '<path d="M4 1.6h4.8L12 4.8V14a.4.4 0 0 1-.4.4H4.4A.4.4 0 0 1 4 14z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>' +
  '<path d="M8.6 1.6v3.2H12" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg>';

function Objective() {
  return (
    <div className="WSMONAD-oa">
      <div className="WSMONAD-oa-input">
        <span className="WSMONAD-oa-prompt">&gt;</span>
        <span className="WSMONAD-oa-text" />
        <span className="WSMONAD-oa-caret" />
      </div>

      <div className="WSMONAD-oa-files">
        <div className="WSMONAD-oa-opt">Your data <span>· optional</span></div>
        <button className="WSMONAD-oa-folder" type="button">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M2 5.5C2 4.7 2.7 4 3.5 4H6l1.2 1.4H12.5C13.3 5.4 14 6 14 6.9V11c0 .8-.7 1.5-1.5 1.5h-9C2.7 12.5 2 11.8 2 11z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
          <span className="WSMONAD-oa-folder-label">Use files from your account</span>
        </button>

        <div className="WSMONAD-oa-drop">
          <div className="WSMONAD-oa-drop-hint">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 10.5V3.4M8 3.4 5.5 5.9M8 3.4l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Drop a file here</span>
          </div>
          <div className="WSMONAD-oa-dropfiles" />
        </div>
      </div>

      <button className="WSMONAD-oa-run" type="button">
        Run investigation <span>↗</span>
      </button>

      <div className="WSMONAD-oa-picker">
        <div className="WSMONAD-oa-phd">your storage</div>
        <div className="WSMONAD-oa-prow"><FileIcon />aged_cohort.fastq</div>
        <div className="WSMONAD-oa-prow"><FileIcon />variants.vcf</div>
        <div className="WSMONAD-oa-prow"><FileIcon />proteome.tsv</div>
      </div>

      <div className="WSMONAD-oa-dragfile"><FileIcon />patients.csv</div>

      <div className="WSMONAD-oa-cursor">
        <svg viewBox="0 0 16 16">
          <path
            d="M 0 0 L 0 12 L 3.6 8.6 L 5.8 13 L 7.8 12 L 5.6 7.6 L 11 7.6 Z"
            fill="var(--ink)"
            stroke="var(--paper)"
            strokeWidth="0.7"
          />
        </svg>
      </div>
    </div>
  );
}

function Domain() {
  return (
    <div className="WSMONAD-dm">
      <span className="WSMONAD-dm-ring" />
      <span className="WSMONAD-dm-ring WSMONAD-r2" />
      <div className="WSMONAD-dm-server">
        <div className="WSMONAD-dm-srv-h">
          <span className="WSMONAD-dm-sphere">
            <svg viewBox="-50 -50 100 100" xmlns="http://www.w3.org/2000/svg" />
          </span>
          <span className="WSMONAD-dm-title">MONAD · domain</span>
        </div>
        <div className="WSMONAD-dm-res">
          <div className="WSMONAD-dm-r WSMONAD-gpu"><span>GPU</span><i><b /></i></div>
          <div className="WSMONAD-dm-r WSMONAD-cpu"><span>CPU</span><i><b /></i></div>
          <div className="WSMONAD-dm-r WSMONAD-sto"><span>STORAGE</span><i><b /></i></div>
        </div>
        <div className="WSMONAD-dm-tiles">
          <div className="WSMONAD-dm-tile">
            <svg className="WSMONAD-dm-ti" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4 1.6h4.8L12 4.8V14a.4.4 0 0 1-.4.4H4.4A.4.4 0 0 1 4 14z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
              <path d="M8.6 1.6v3.2H12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
            <b className="WSMONAD-dm-files">0</b>
            <span>files uploaded</span>
          </div>
          <div className="WSMONAD-dm-tile">
            <svg className="WSMONAD-dm-ti" viewBox="0 0 16 16" aria-hidden="true">
              <ellipse cx="8" cy="4" rx="5" ry="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
              <path d="M3 4v8c0 1.1 2.2 2 5 2s5-.9 5-2V4" fill="none" stroke="currentColor" strokeWidth="1.2" />
              <path d="M3 8.2c0 1.1 2.2 2 5 2s5-.9 5-2" fill="none" stroke="currentColor" strokeWidth="1.1" />
            </svg>
            <b className="WSMONAD-dm-count">0</b>
            <span>databases mounted</span>
          </div>
        </div>
      </div>
      <div className="WSMONAD-dm-terms">
        <div className="WSMONAD-dm-proc"><span className="WSMONAD-dm-pdot" /><div className="WSMONAD-dm-pb" /></div>
        <div className="WSMONAD-dm-proc"><span className="WSMONAD-dm-pdot" /><div className="WSMONAD-dm-pb" /></div>
      </div>
    </div>
  );
}

function Orchestration() {
  return (
    <svg className="WSMONAD-da-svg" viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <g className="WSMONAD-or-cam">
        {ORCH.map((n, i) => {
          if (i === 0) return null;
          const p = ORCH[n.parent];
          const len = Math.hypot(n.x - p.x, n.y - p.y).toFixed(1);
          return (
            <line
              key={`b${i}`}
              className="WSMONAD-or-branch"
              x1={(CX + p.x).toFixed(1)}
              y1={(CY + p.y).toFixed(1)}
              x2={(CX + n.x).toFixed(1)}
              y2={(CY + n.y).toFixed(1)}
              data-gen={n.gen}
              style={{ strokeDasharray: len, strokeDashoffset: len }}
            />
          );
        })}
        {ORCH_LINKS.map((m, i) => {
          const a = ORCH[m.a];
          const b = ORCH[m.b];
          return (
            <line
              key={`m${i}`}
              className="WSMONAD-or-merge"
              x1={(CX + a.x).toFixed(1)}
              y1={(CY + a.y).toFixed(1)}
              x2={(CX + b.x).toFixed(1)}
              y2={(CY + b.y).toFixed(1)}
              style={{ strokeDasharray: m.len, strokeDashoffset: m.len }}
            />
          );
        })}
        {ORCH.map((n, i) => (
          <circle
            key={`n${i}`}
            className={i === 0 ? 'WSMONAD-or-node WSMONAD-or-root' : 'WSMONAD-or-node'}
            cx={(CX + n.x).toFixed(1)}
            cy={(CY + n.y).toFixed(1)}
            r={i === 0 ? 5.5 : n.gen === 1 ? 3.2 : n.gen === 2 ? 2.4 : n.gen === 3 ? 1.9 : 1.5}
            data-gen={n.gen}
          />
        ))}
        {ORCH_TERMS.map((idx) => {
          const n = ORCH[idx];
          const left = orchHash(idx) > 0.5;
          const tx = CX + n.x + (left ? -20.5 : 5.5);
          const ty = CY + n.y - 8;
          return (
            <g key={`t${idx}`} transform={`translate(${tx.toFixed(1)},${ty.toFixed(1)})`}>
              <g className="WSMONAD-or-term" data-term={idx}>
                <rect className="WSMONAD-or-tw" x="0" y="0" width="15" height="10.5" rx="1.5" />
                <rect className="WSMONAD-or-tb" x="2.2" y="2.3" width="7" height="1.3" rx="0.6" style={{ animationDelay: '0s' }} />
                <rect className="WSMONAD-or-tb" x="2.2" y="4.9" width="10.4" height="1.3" rx="0.6" style={{ animationDelay: '.45s' }} />
                <rect className="WSMONAD-or-tb" x="2.2" y="7.5" width="5.6" height="1.3" rx="0.6" style={{ animationDelay: '.9s' }} />
              </g>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

// Small format glyphs for the artifact cards (16x16, currentColor).
const RS_GLYPH = {
  doc:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 1.7h4.6L12 5.1V14a.4.4 0 0 1-.4.4H4.4A.4.4 0 0 1 4 14z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/><path d="M8.5 1.7v3.3H12" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/><path d="M5.7 8.4h4.6M5.7 10.4h4.6M5.7 12.2h3" stroke="currentColor" stroke-width="0.85" stroke-linecap="round"/></svg>',
  table:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.3" y="3.2" width="11.4" height="9.6" rx="1" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M2.3 6.4h11.4M2.3 9.6h11.4M6.1 3.2v9.6M9.9 3.2v9.6" stroke="currentColor" stroke-width="0.8"/></svg>',
  chart:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.6 2.6v10.8h10.8" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/><rect x="4.4" y="8" width="1.9" height="4" fill="currentColor"/><rect x="7.6" y="5.3" width="1.9" height="6.7" fill="currentColor"/><rect x="10.8" y="9.4" width="1.9" height="2.6" fill="currentColor"/></svg>',
  code:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4.6 2.9 8 6 11.4M10 4.6 13.1 8 10 11.4" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  folder:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 5.2C2 4.5 2.6 4 3.3 4H6l1.2 1.4h5.5c.7 0 1.3.6 1.3 1.3v4.8c0 .7-.6 1.3-1.3 1.3H3.3C2.6 12.8 2 12.2 2 11.5z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg>',
  graph:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.6 5 8 8.1M8 8.1 11.8 5.4M8 8.1 6.6 12.2" stroke="currentColor" stroke-width="0.9"/><circle cx="4.6" cy="4.6" r="1.8" fill="currentColor"/><circle cx="11.8" cy="5.1" r="1.6" fill="currentColor"/><circle cx="6.6" cy="12.3" r="1.5" fill="currentColor"/><circle cx="8" cy="8.1" r="1.5" fill="currentColor"/></svg>',
  data:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="4" rx="4.5" ry="1.9" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M3.5 4v8c0 1 2 1.9 4.5 1.9s4.5-.9 4.5-1.9V4" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M3.5 8c0 1 2 1.9 4.5 1.9s4.5-.9 4.5-1.9" fill="none" stroke="currentColor" stroke-width="0.85"/></svg>',
  image:
    '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.1"/><circle cx="5.6" cy="6.4" r="1.1" fill="currentColor"/><path d="M3.2 11.8 6.4 8.6l2.1 1.9 2.3-2.7 2.2 2.9" fill="none" stroke="currentColor" stroke-width="1"/></svg>',
};
// A deliberately mixed bag: data, figures, scripts, graphs, reports, subfolders.
// The variety is the point: MONAD writes out whatever the goal needs, any format.
// One artifact is authored at a time, cycling through formats so the variety
// reads as "any format you need". The glyph in the title bar is keyed by type.
const RS_ARTIFACTS = [
  { name: 'report.pdf', type: 'doc' },
  { name: 'targets.csv', type: 'table' },
  { name: 'volcano.png', type: 'chart' },
  { name: 'pipeline.py', type: 'code' },
  { name: 'cohort.tsv', type: 'table' },
  { name: 'pathways.svg', type: 'chart' },
];

function Resolution() {
  return (
    <div className="WSMONAD-rs">
      <div className="WSMONAD-rs-stage">
        <div className="WSMONAD-rs-file">
          <div className="WSMONAD-rs-bar">
            <span className="WSMONAD-rs-ic" />
            <span className="WSMONAD-rs-nm" />
            <svg className="WSMONAD-rs-ck" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="10" cy="10" r="9" />
              <path
                d="M5.8 10.3 8.6 13l5.5-6.3"
                fill="none"
                stroke="var(--paper)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="WSMONAD-rs-body" />
        </div>
      </div>
      <div className="WSMONAD-rs-folder">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M2 5.2C2 4.5 2.6 4 3.3 4H6l1.2 1.4h5.5c.7 0 1.3.6 1.3 1.3v4.8c0 .7-.6 1.3-1.3 1.3H3.3C2.6 12.8 2 12.2 2 11.5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
        <span className="WSMONAD-rs-fl">artifacts/</span>
        <b className="WSMONAD-rs-n">0</b>
      </div>
    </div>
  );
}

// Scripted ask-composer loop for the objective panel.
function runObjective(root) {
  const q = (s) => root.querySelector(s);
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cursor = q('.WSMONAD-oa-cursor');
  const text = q('.WSMONAD-oa-text');
  const input = q('.WSMONAD-oa-input');
  const files = q('.WSMONAD-oa-files');
  const folder = q('.WSMONAD-oa-folder');
  const drop = q('.WSMONAD-oa-drop');
  const dropfiles = q('.WSMONAD-oa-dropfiles');
  const runBtn = q('.WSMONAD-oa-run');
  const picker = q('.WSMONAD-oa-picker');
  const dragfile = q('.WSMONAD-oa-dragfile');
  if (!cursor || !text) return () => {};

  const GOALS = [
    'find drivers of anti-PD-1 resistance',
    'find druggable targets in PDAC',
    'map eQTLs regulating APOE in brain',
    'find synthetic-lethal partners of TP53',
  ];
  const EASE = 'cubic-bezier(.6,0,.3,1)';

  const timers = [];
  const at = (ms, fn) => timers.push(setTimeout(fn, ms));
  const rootRect = () => root.getBoundingClientRect();
  function center(el) {
    const r = el.getBoundingClientRect();
    const b = rootRect();
    return { x: r.left - b.left + r.width / 2, y: r.top - b.top + r.height / 2 };
  }
  function moveCursorXY(x, y, dur = 560) {
    cursor.style.transition = `transform ${dur}ms ${EASE}, opacity .3s ease`;
    cursor.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
  }
  function moveCursor(el, dur = 560) {
    if (!el) return;
    const c = center(el);
    moveCursorXY(c.x, c.y, dur);
  }
  function showCursor() {
    cursor.style.transition = 'opacity .3s ease';
    cursor.classList.add('WSMONAD-show');
  }
  function clickFx() {
    cursor.classList.add('WSMONAD-clicking');
    at(180, () => cursor.classList.remove('WSMONAD-clicking'));
  }
  function placeDrag(x, y, dur) {
    const w = dragfile.offsetWidth || 78;
    const h = dragfile.offsetHeight || 22;
    dragfile.style.transition = dur
      ? `transform ${dur}ms cubic-bezier(.5,0,.2,1), opacity .25s ease`
      : 'opacity .25s ease';
    dragfile.style.transform = `translate(${(x - w / 2).toFixed(1)}px, ${(y - h / 2).toFixed(1)}px)`;
  }
  function openPicker() {
    const f = folder.getBoundingClientRect();
    const b = rootRect();
    picker.style.left = `${(f.left - b.left).toFixed(0)}px`;
    picker.style.top = `${(f.bottom - b.top + 6).toFixed(0)}px`;
    picker.classList.add('WSMONAD-in');
  }
  function addDropFile(name) {
    drop.classList.add('WSMONAD-has');
    const chip = document.createElement('span');
    chip.className = 'WSMONAD-oa-fchip';
    chip.innerHTML = `${FILE_SVG}<span>${name}</span>`;
    dropfiles.appendChild(chip);
    requestAnimationFrame(() => chip.classList.add('WSMONAD-in'));
  }

  function reset() {
    text.textContent = '';
    files.classList.remove('WSMONAD-show');
    runBtn.classList.remove('WSMONAD-show', 'WSMONAD-fire');
    input.classList.remove('WSMONAD-active');
    folder.classList.remove('WSMONAD-active');
    drop.classList.remove('WSMONAD-has', 'WSMONAD-over');
    dropfiles.innerHTML = '';
    picker.classList.remove('WSMONAD-in');
    picker.querySelectorAll('.WSMONAD-oa-prow').forEach((r) => r.classList.remove('WSMONAD-picked'));
    cursor.classList.remove('WSMONAD-show', 'WSMONAD-clicking');
    dragfile.classList.remove('WSMONAD-show', 'WSMONAD-grab');
    const b = rootRect();
    cursor.style.transition = 'none';
    cursor.style.transform = `translate(${(b.width * 0.5).toFixed(0)}px, ${(b.height * 0.6).toFixed(0)}px)`;
    placeDrag(b.width - 34, 16, 0);
  }

  function typeGoal(goal, done) {
    let i = 0;
    const step = () => {
      i += 1;
      text.textContent = goal.slice(0, i);
      if (i < goal.length) at(38, step);
      else if (done) at(0, done);
    };
    step();
  }

  let gi = 0;
  function play() {
    reset();
    const goal = GOALS[gi % GOALS.length];
    gi += 1;

    if (reduce) {
      text.textContent = goal;
      files.classList.add('WSMONAD-show');
      runBtn.classList.add('WSMONAD-show');
      folder.classList.add('WSMONAD-active');
      addDropFile('aged_cohort.fastq');
      addDropFile('patients.csv');
      return;
    }

    at(320, () => input.classList.add('WSMONAD-active'));
    at(560, () =>
      typeGoal(goal, () => {
        at(220, () => files.classList.add('WSMONAD-show'));
        at(820, showCursor);
        at(1100, () => moveCursor(folder, 560));
        at(1720, () => {
          clickFx();
          folder.classList.add('WSMONAD-active');
          openPicker();
        });
        at(2120, () => moveCursor(picker.querySelectorAll('.WSMONAD-oa-prow')[0], 440));
        at(2600, () => {
          clickFx();
          const r0 = picker.querySelectorAll('.WSMONAD-oa-prow')[0];
          if (r0) r0.classList.add('WSMONAD-picked');
          addDropFile('aged_cohort.fastq');
        });
        at(3060, () => picker.classList.remove('WSMONAD-in'));
        at(3240, () => dragfile.classList.add('WSMONAD-show'));
        at(3440, () => moveCursor(dragfile, 520));
        at(4000, () => dragfile.classList.add('WSMONAD-grab'));
        at(4180, () => {
          const c = center(drop);
          moveCursorXY(c.x, c.y, 660);
          placeDrag(c.x, c.y, 660);
        });
        at(4720, () => drop.classList.add('WSMONAD-over'));
        at(4960, () => {
          dragfile.classList.remove('WSMONAD-show', 'WSMONAD-grab');
          drop.classList.remove('WSMONAD-over');
          addDropFile('patients.csv');
        });
        at(5320, () => runBtn.classList.add('WSMONAD-show'));
        at(5620, () => moveCursor(runBtn, 520));
        at(6260, () => {
          clickFx();
          runBtn.classList.add('WSMONAD-fire');
        });
      }),
    );

    at(10600, play);
  }

  at(80, play);
  return () => timers.forEach(clearTimeout);
}

// Loop: MONAD assembles big in the CENTRE -> glides top-left as the server
// provisions (GPU/CPU/storage + uploaded-files / mounted-databases tiles) ->
// two short terminals slide out and open/close on the right (live processes) ->
// returns to the centred MONAD and repeats.
function runDomain(root) {
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const dm = root.querySelector('.WSMONAD-dm');
  const sphere = root.querySelector('.WSMONAD-dm-sphere');
  const sphereSvg = sphere && sphere.querySelector('svg');
  const count = root.querySelector('.WSMONAD-dm-count');
  const filesEl = root.querySelector('.WSMONAD-dm-files');
  const procs = Array.from(root.querySelectorAll('.WSMONAD-dm-proc'));

  let sphereStop = null;
  function buildSphere(assemble) {
    if (!sphereSvg) return;
    if (sphereStop) sphereStop();
    sphereSvg.innerHTML = '';
    sphereStop = makePointCloud(sphereSvg, { N: 60, R: 42, assemble, assembleMs: 1400 });
  }
  if (!dm || !sphere || !procs.length) {
    buildSphere(false);
    return () => (sphereStop ? sphereStop() : undefined);
  }

  const MOUNT = [14, 28, 42, 55, 66, 75, 82];
  const rnd = (n) => Math.floor(Math.random() * n);
  const timers = [];
  const at = (ms, fn) => timers.push(setTimeout(fn, ms));
  let procTimers = [];
  const pat = (ms, fn) => {
    const t = setTimeout(fn, ms);
    procTimers.push(t);
    timers.push(t);
    return t;
  };

  // sphere lives in the header (top-left); transform it to the panel centre
  let dockCx = 0;
  let dockCy = 0;
  function measureDock() {
    const b = root.getBoundingClientRect();
    const s = sphere.getBoundingClientRect();
    dockCx = s.left - b.left + s.width / 2;
    dockCy = s.top - b.top + s.height / 2;
  }
  function centerXform(scale) {
    const b = root.getBoundingClientRect();
    return `translate(${(b.width / 2 - dockCx).toFixed(0)}px, ${(b.height / 2 - dockCy).toFixed(0)}px) scale(${scale})`;
  }

  function typeProc(body, lines, done) {
    let l = 0;
    const nextLn = () => {
      if (l >= lines) {
        done();
        return;
      }
      const ln = document.createElement('div');
      ln.className = 'WSMONAD-dm-bl';
      body.appendChild(ln);
      const n = 3 + rnd(4);
      let i = 0;
      const addBar = () => {
        const bar = document.createElement('span');
        let cls = 'WSMONAD-dm-bar';
        if (i === 0) cls += ' WSMONAD-k';
        else if (i === n - 1 && Math.random() < 0.5) cls += ' WSMONAD-o';
        else if (Math.random() < 0.16) cls += ' WSMONAD-k';
        bar.className = cls;
        bar.style.width = `${7 + rnd(12)}px`;
        ln.appendChild(bar);
        i += 1;
        if (i < n) pat(26, addBar);
        else {
          l += 1;
          pat(80, nextLn);
        }
      };
      pat(34, addBar);
    };
    nextLn();
  }
  function runProc(proc, delay) {
    const body = proc.querySelector('.WSMONAD-dm-pb');
    if (!body) return;
    const cycle = () => {
      body.innerHTML = '';
      proc.classList.add('WSMONAD-open');
      typeProc(body, 2 + rnd(2), () => {
        pat(380 + rnd(400), () => {
          proc.classList.remove('WSMONAD-open');
          pat(380 + rnd(600), cycle);
        });
      });
    };
    pat(delay, cycle);
  }
  function startProcs() {
    procs.forEach((p, i) => runProc(p, i * 600));
  }
  function stopProcs() {
    procTimers.forEach(clearTimeout);
    procTimers = [];
    procs.forEach((p) => {
      p.classList.remove('WSMONAD-open');
      const bd = p.querySelector('.WSMONAD-dm-pb');
      if (bd) bd.innerHTML = '';
    });
  }
  function tickCounts() {
    MOUNT.forEach((v, k) => at(300 + k * 140, () => { if (count) count.textContent = String(v); }));
    at(500, () => { if (filesEl) filesEl.textContent = '1'; });
    at(760, () => { if (filesEl) filesEl.textContent = '2'; });
  }
  function resetCounts() {
    if (count) count.textContent = '0';
    if (filesEl) filesEl.textContent = '0';
  }

  if (reduce) {
    sphere.style.transition = 'none';
    sphere.style.transform = 'none';
    buildSphere(false);
    dm.classList.add('WSMONAD-srv', 'WSMONAD-prov');
    if (count) count.textContent = '82';
    if (filesEl) filesEl.textContent = '2';
    procs.forEach((p) => {
      p.classList.add('WSMONAD-open');
      const bd = p.querySelector('.WSMONAD-dm-pb');
      if (!bd) return;
      for (let l = 0; l < 2; l += 1) {
        const ln = document.createElement('div');
        ln.className = 'WSMONAD-dm-bl';
        for (let i = 0; i < 5; i += 1) {
          const bar = document.createElement('span');
          bar.className = i === 0 ? 'WSMONAD-dm-bar WSMONAD-k' : 'WSMONAD-dm-bar';
          bar.style.width = `${7 + ((i * 5) % 12)}px`;
          bar.style.opacity = '0.85';
          ln.appendChild(bar);
        }
        bd.appendChild(ln);
      }
    });
    return () => (sphereStop ? sphereStop() : undefined);
  }

  // setup: measure docked spot, then sit the sphere centred + large
  sphere.style.transition = 'none';
  sphere.style.transform = 'none';
  measureDock();
  sphere.style.transform = centerXform(1.9);

  function toServer() {
    sphere.style.transition = 'transform 0.9s cubic-bezier(.5,0,.2,1)';
    sphere.style.transform = 'none'; // dock top-left
    dm.classList.add('WSMONAD-srv', 'WSMONAD-prov');
    tickCounts();
  }
  function toCenter() {
    stopProcs();
    dm.classList.remove('WSMONAD-srv', 'WSMONAD-prov', 'WSMONAD-pulse');
    resetCounts();
    buildSphere(true); // re-assemble
    sphere.style.transition = 'transform 0.8s cubic-bezier(.5,0,.2,1)';
    sphere.style.transform = centerXform(1.9); // back to centre, big
  }
  function cycle() {
    // pulse while centred: expand then settle, with the ring
    at(1650, () => {
      dm.classList.add('WSMONAD-pulse');
      sphere.style.transition = 'transform 0.5s ease-in-out';
      sphere.style.transform = centerXform(2.15);
      at(560, () => { sphere.style.transform = centerXform(1.9); });
    });
    // only after the pulse: dock top-left + provision
    at(3050, toServer);
    at(3550, startProcs);
    at(11200, toCenter);
    at(12000, cycle);
  }

  let io = null;
  let started = false;
  const begin = () => {
    if (started) return;
    started = true;
    buildSphere(true); // assemble in the centre on first view
    cycle();
  };
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          begin();
          if (io) {
            io.disconnect();
            io = null;
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(root);
  } else {
    begin();
  }

  return () => {
    timers.forEach(clearTimeout);
    if (sphereStop) sphereStop();
    if (io) io.disconnect();
  };
}

// Orchestration: a single branch tree grows out from the MONAD core. It starts
// slow and zoomed in on the core, then each generation of branches spawns faster
// than the last while the camera eases outward to keep the widening tree framed.
function runOrch(root) {
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cam = root.querySelector('.WSMONAD-or-cam');
  const branches = Array.from(root.querySelectorAll('.WSMONAD-or-branch'));
  const nodes = Array.from(root.querySelectorAll('.WSMONAD-or-node'));
  const merges = Array.from(root.querySelectorAll('.WSMONAD-or-merge'));
  const terms = Array.from(root.querySelectorAll('.WSMONAD-or-term'));
  const termByNode = {};
  terms.forEach((el) => { termByNode[el.getAttribute('data-term')] = el; });
  if (!cam || nodes.length < 2) return () => {};

  // camera scale per generation: tight on the core, then easing further out each
  // time so the whole widening tree stays framed (viewBox half-height 90). The
  // pull-back also quickens generation to generation.
  const FIT = 76;
  const CAM = { 0: 2.4 };
  [1, 2, 3, 4].forEach((g) => {
    const r = ORCH_RADIUS[g];
    CAM[g] = r ? Math.min(1.85, FIT / r) : CAM[g - 1] || 1;
  });
  const CAM_DUR = { 1: 1600, 2: 1300, 3: 1050, 4: 850 };
  const timers = [];
  const at = (ms, fn) => timers.push(setTimeout(fn, ms));

  function setCam(scale, dur, ease) {
    cam.style.transition = dur ? `transform ${dur}ms ${ease || 'cubic-bezier(.4,0,.25,1)'}` : 'none';
    cam.style.transform = `scale(${scale})`;
  }
  function popNode(i) {
    const n = nodes[i];
    if (!n) return;
    n.style.transition = 'transform .34s cubic-bezier(.34,1.5,.5,1)';
    n.style.transform = 'scale(1)';
  }
  function popTerm(i) {
    const el = termByNode[String(i)];
    if (!el) return;
    el.style.transition = 'transform .32s cubic-bezier(.34,1.5,.5,1)';
    el.style.transform = 'scale(1)';
  }
  function drawBranch(i) {
    const b = branches[i - 1];
    if (!b) return;
    b.style.transition = 'stroke-dashoffset .44s ease-out';
    b.style.strokeDashoffset = '0';
  }
  function drawMerge(m) {
    m.style.transition = 'stroke-dashoffset .5s ease-out';
    m.style.strokeDashoffset = '0';
  }
  function retractNode(i) {
    const n = nodes[i];
    if (n) {
      // slide the node inward toward its parent while it shrinks, so the tree
      // visibly collapses toward the core rather than dissolving in place
      const node = ORCH[i];
      const par = ORCH[node.parent] || { x: 0, y: 0 };
      const dx = (par.x - node.x) * 0.6;
      const dy = (par.y - node.y) * 0.6;
      n.style.transition = 'transform .34s cubic-bezier(.5,0,.75,.4)';
      n.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(0)`;
    }
    const b = branches[i - 1];
    if (b) {
      b.style.transition = 'stroke-dashoffset .32s ease-in';
      b.style.strokeDashoffset = b.style.strokeDasharray || '60';
    }
    const el = termByNode[String(i)];
    if (el) {
      el.style.transition = 'transform .24s ease-in';
      el.style.transform = 'scale(0)';
    }
  }
  function reset() {
    setCam(CAM[0], 0);
    nodes.forEach((n, i) => {
      n.style.transition = 'none';
      n.style.transform = i === 0 ? 'scale(1)' : 'scale(0)'; // core stays put
    });
    terms.forEach((el) => {
      el.style.transition = 'none';
      el.style.transform = 'scale(0)';
    });
    branches.forEach((b) => {
      b.style.transition = 'none';
      b.style.strokeDashoffset = b.style.strokeDasharray || '60';
    });
    merges.forEach((m) => {
      m.style.transition = 'none';
      m.style.strokeDashoffset = m.style.strokeDasharray || '20';
    });
    void cam.getBoundingClientRect();
  }
  // De-assemble the tree back into the core, and make the camera FOLLOW the
  // deconstruction front rather than lead it. Nodes retract outermost-first,
  // paced by radius (so the outer edge shrinks at a steady speed), and the zoom
  // is pinned to the radius of the outermost node still standing, so the
  // remaining tree always fills the frame. The zoom therefore cannot outrun the
  // fold-up (it is a function of it). Then run `done` (regrow) for a seamless loop.
  function collapse(done) {
    const FIT = 76; // matches the expanded framing, so the camera starts continuous
    const D = 1400;
    const lead = 340; // last node's ~.34s retract lands as the camera arrives
    const usable = D - lead;
    const order = [];
    for (let i = 1; i < nodes.length; i += 1) order.push(i);
    order.sort((a, b) => Math.hypot(ORCH[b].x, ORCH[b].y) - Math.hypot(ORCH[a].x, ORCH[a].y));
    const radii = order.map((i) => Math.hypot(ORCH[i].x, ORCH[i].y) || 1);
    const n = radii.length;
    const maxR = radii[0];
    const minR = radii[n - 1];
    const rng = maxR - minR || 1;

    merges.forEach((m) => {
      m.style.transition = 'stroke-dashoffset .3s ease-in';
      m.style.strokeDashoffset = m.style.strokeDasharray || '20';
    });

    const times = radii.map((r) => ((maxR - r) / rng) * usable);
    order.forEach((idx, k) => {
      const t = times[k];
      const nextR = k + 1 < n ? radii[k + 1] : minR * 0.5;
      const targetScale = Math.min(CAM[0], FIT / nextR);
      const dur = Math.max(70, k + 1 < n ? times[k + 1] - t : lead);
      at(t, () => {
        retractNode(idx);
        setCam(targetScale, dur, 'linear'); // follow the shrinking outer edge
      });
    });
    at(D + 120, done);
  }

  if (reduce) {
    setCam(CAM[4], 0);
    nodes.forEach((n) => { n.style.transform = 'scale(1)'; });
    terms.forEach((el) => { el.style.transform = 'scale(1)'; });
    branches.forEach((b) => { b.style.strokeDashoffset = '0'; });
    merges.forEach((m) => { m.style.strokeDashoffset = '0'; });
    return () => {};
  }

  function grow() {
    reset();
    at(60, () => setCam(CAM[1], CAM_DUR[1]));

    // schedule each node: the interval shrinks node to node, so the tree fills
    // out faster and faster as it builds
    const sched = [];
    let t = 360;
    let interval = 285;
    for (let i = 1; i < nodes.length; i += 1) {
      sched.push({ idx: i, gen: +nodes[i].getAttribute('data-gen'), t });
      t += interval;
      interval = Math.max(20, interval * 0.91);
    }

    // ease the camera outward (quicker each time) as each new generation appears
    const seenGen = {};
    sched.forEach(({ gen, t: tt }) => {
      if (seenGen[gen]) return;
      seenGen[gen] = true;
      at(tt, () => setCam(CAM[gen], CAM_DUR[gen] || 900));
    });

    // grow each branch, pop its node, and any terminal beside it
    sched.forEach(({ idx, t: tt }) => {
      at(tt, () => { drawBranch(idx); popNode(idx); popTerm(idx); });
    });

    // separate branches combine: draw the cross-branch merges as the tree fills
    merges.forEach((m, k) => at(t + 120 + k * 70, () => drawMerge(m)));

    // brief beat on the full tree, then de-assemble into the core + zoom in (loop)
    const mergesEnd = t + 120 + merges.length * 70;
    at(mergesEnd + 500, () => collapse(grow));
  }

  let io = null;
  let started = false;
  const begin = () => {
    if (started) return;
    started = true;
    grow();
  };
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          begin();
          if (io) {
            io.disconnect();
            io = null;
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(root);
  } else {
    begin();
  }

  return () => {
    timers.forEach(clearTimeout);
    if (io) io.disconnect();
  };
}

// Resolution: MONAD authors one deliverable at a time, live, in the centre of
// the panel. You watch the page fill in (a report's text, a table's rows, a
// chart's bars, a script's lines), it gets a done check, then it drops into the
// artifacts/ folder and the count ticks up. Different format each pass, on a loop.
// No labels needed: the act of writing the file is the message.
function runResolution(root) {
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const file = root.querySelector('.WSMONAD-rs-file');
  const ic = root.querySelector('.WSMONAD-rs-ic');
  const nm = root.querySelector('.WSMONAD-rs-nm');
  const body = root.querySelector('.WSMONAD-rs-body');
  const folder = root.querySelector('.WSMONAD-rs-folder');
  const nEl = root.querySelector('.WSMONAD-rs-n');
  if (!file || !body) return () => {};

  const timers = [];
  const at = (ms, fn) => timers.push(setTimeout(fn, ms));
  const el = (cls, parent) => {
    const d = document.createElement('div');
    d.className = cls;
    (parent || body).appendChild(d);
    return d;
  };
  const reveal = (node, delay) => at(delay, () => node.classList.add('WSMONAD-in'));

  // each builder authors a different format into the page, live, and returns how
  // long (ms) that authoring takes. The act of writing IS the message.
  function buildDoc() {
    const r = el('WSMONAD-rs-doc');
    const widths = [94, 80, 88, 66, 82];
    widths.forEach((w, i) => {
      const ln = el('WSMONAD-rs-ln', r);
      ln.style.width = `${w}%`;
      reveal(ln, 120 + i * 120);
    });
    return 120 + widths.length * 120;
  }
  function buildTable() {
    const r = el('WSMONAD-rs-tbl');
    const rows = 4;
    const cols = 4;
    for (let ri = 0; ri < rows; ri += 1) {
      const row = el('WSMONAD-rs-trow', r);
      for (let c = 0; c < cols; c += 1) {
        const cell = document.createElement('span');
        cell.className = `WSMONAD-rs-cell${ri === 0 ? ' WSMONAD-h' : ''}`;
        row.appendChild(cell);
      }
      reveal(row, 120 + ri * 150);
    }
    return 120 + rows * 150;
  }
  function buildChart() {
    const r = el('WSMONAD-rs-chart');
    const hs = [42, 72, 54, 90, 66];
    hs.forEach((h, i) => {
      const b = document.createElement('span');
      b.className = 'WSMONAD-rs-cbar';
      r.appendChild(b);
      at(160 + i * 120, () => { b.style.height = `${h}%`; });
    });
    return 160 + hs.length * 120;
  }
  function buildCode() {
    const r = el('WSMONAD-rs-code');
    const counts = [3, 4, 3, 5, 4];
    const indent = [0, 12, 12, 22, 8];
    counts.forEach((n, i) => {
      const line = el('WSMONAD-rs-cl', r);
      line.style.marginLeft = `${indent[i]}px`;
      for (let t = 0; t < n; t += 1) {
        const tk = document.createElement('span');
        tk.className = `WSMONAD-rs-tok${t === 0 ? ' WSMONAD-k' : ''}`;
        tk.style.width = `${9 + ((i * 4 + t * 6) % 16)}px`;
        line.appendChild(tk);
      }
      reveal(line, 120 + i * 120);
    });
    return 120 + counts.length * 120;
  }
  const BUILD = { doc: buildDoc, table: buildTable, chart: buildChart, code: buildCode };

  if (reduce) {
    const a = RS_ARTIFACTS[0];
    if (ic) ic.innerHTML = RS_GLYPH[a.type] || RS_GLYPH.doc;
    if (nm) nm.textContent = a.name;
    file.classList.add('WSMONAD-in', 'WSMONAD-done');
    const r = el('WSMONAD-rs-doc');
    [94, 80, 88, 66, 82].forEach((w) => {
      const ln = el('WSMONAD-rs-ln', r);
      ln.style.width = `${w}%`;
      ln.classList.add('WSMONAD-in');
    });
    if (nEl) nEl.textContent = '3';
    return () => {};
  }

  let idx = 0;
  let count = 0;
  function play() {
    const a = RS_ARTIFACTS[idx % RS_ARTIFACTS.length];
    const fresh = idx % RS_ARTIFACTS.length === 0;
    idx += 1;

    // reset to a blank page with no transition, then pop the new file in
    file.style.transition = 'none';
    file.classList.remove('WSMONAD-in', 'WSMONAD-done', 'WSMONAD-file-out');
    body.innerHTML = '';
    if (ic) ic.innerHTML = RS_GLYPH[a.type] || RS_GLYPH.doc;
    if (nm) nm.textContent = a.name;
    if (fresh) {
      count = 0;
      if (nEl) nEl.textContent = '0';
    }
    void file.offsetWidth;
    file.style.transition = '';
    requestAnimationFrame(() => file.classList.add('WSMONAD-in'));

    // author the content, then mark it done, then file it into the folder
    const dur = (BUILD[a.type] || buildDoc)();
    at(dur + 250, () => file.classList.add('WSMONAD-done'));
    at(dur + 800, () => {
      file.classList.add('WSMONAD-file-out');
      count += 1;
      if (nEl) nEl.textContent = String(count);
      if (folder) {
        folder.classList.add('WSMONAD-bump');
        at(320, () => folder.classList.remove('WSMONAD-bump'));
      }
    });
    at(dur + 1380, play);
  }

  let io = null;
  let started = false;
  const begin = () => {
    if (started) return;
    started = true;
    play();
  };
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          begin();
          if (io) {
            io.disconnect();
            io = null;
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(root);
  } else {
    begin();
  }

  return () => {
    timers.forEach(clearTimeout);
    if (io) io.disconnect();
  };
}

export default function DoesAnim({ phase }) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    if (phase === 'objective') return runObjective(root);
    if (phase === 'domain') return runDomain(root);
    if (phase === 'orchestration') return runOrch(root);
    if (phase === 'resolution') return runResolution(root);
    return undefined;
  }, [phase]);

  return (
    <div className="WSMONAD-da" data-phase={phase} ref={ref}>
      {phase === 'objective' && <Objective />}
      {phase === 'domain' && <Domain />}
      {phase === 'orchestration' && <Orchestration />}
      {phase === 'resolution' && <Resolution />}
    </div>
  );
}
